import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLanguage } from "@/i18n";
import LogoMark from "@/components/Logo";
import useMenu from "@/hooks/useMenu";
import { api } from "@/lib/api";

export default function Navbar() {
  const { lang, setLang, t } = useLanguage();
  const menuItems = useMenu();
  const [langSwitch, setLangSwitch] = useState(true);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    api.get("/settings").then((res) => setLangSwitch(res.data.language_switch_enabled !== false)).catch(() => {});
  }, []);

  useEffect(() => {
    if (langSwitch === false && lang !== "it") setLang("it");
  }, [langSwitch, lang]);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const links = menuItems.map((m) => ({
    to: m.path,
    label: (lang === "en" && m.label_en) || m.label,
    testid: `nav-link-${m.key}`,
    key: m.key,
  }));

  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-white/85 backdrop-blur-md border-b border-slate-200">
      <div className="container-editorial flex h-16 sm:h-20 items-center justify-between xl:px-8">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo-link">
          <LogoMark className="h-12 sm:h-14 w-auto" />
          <span className="leading-tight">
            <span className="block font-serif text-lg sm:text-xl text-slate-900">Dott. Giovanni Angilè</span>
            <span className="block text-[10px] uppercase tracking-[0.22em] text-brand">
              Omeopatia · Neuralterapia · Medicina naturale
            </span>
          </span>
        </Link>

        <nav className="hidden xl:flex items-center gap-6">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={({ isActive }) =>
                `text-sm font-medium link-underline transition-colors duration-300 ${
                  isActive ? "text-brand" : "text-slate-600 hover:text-slate-900"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          {langSwitch && (
            <button
              data-testid="nav-language-toggle"
              onClick={() => setLang(lang === "it" ? "en" : "it")}
              className="whitespace-nowrap text-xs font-semibold tracking-widest text-slate-500 border border-slate-200 rounded-full px-3 py-1.5 hover:border-brand hover:text-brand transition-colors duration-300"
              aria-label="Cambia lingua / Change language"
            >
              {lang === "it" ? "IT / EN" : "EN / IT"}
            </button>
          )}
          <Link
            to="/contatti"
            data-testid="nav-cta-button"
            className="whitespace-nowrap rounded-full bg-brand px-5 py-2.5 text-sm font-medium text-white hover:bg-brand-dark transition-colors duration-300"
          >
            {t.nav.prenota}
          </Link>
        </nav>

        <div className="flex xl:hidden items-center gap-3">
          {langSwitch && (
            <button
              data-testid="nav-language-toggle-mobile"
              onClick={() => setLang(lang === "it" ? "en" : "it")}
              className="text-xs font-semibold tracking-widest text-slate-500 border border-slate-200 rounded-full px-3 py-1.5"
            >
              {lang === "it" ? "IT / EN" : "EN / IT"}
            </button>
          )}
          <button
            data-testid="nav-mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="p-2 text-slate-700"
            aria-label={open ? t.nav.chiudi : t.nav.menu}
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="xl:hidden overflow-hidden bg-white border-b border-slate-200"
          >
            <div className="container-editorial py-6 flex flex-col gap-1">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={l.testid}
                  className={({ isActive }) =>
                    `py-3 text-lg font-serif block ${
                      isActive ? "text-brand" : "text-slate-700"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/contatti"
                data-testid="nav-mobile-cta-button"
                className="mt-4 rounded-full bg-brand px-5 py-3 text-sm font-medium text-white text-center"
              >
                {t.nav.prenota}
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
